import {
  GA_CONSENT_CHOICE,
  GA_EVENTS,
  GA_PARAMS,
  type GaConsentChoice,
  type GaConsentSource,
  type GaEventName,
  type GaLinkPlacement,
} from '@/constants/analytics';
import { COOKIES_CONSENT_VALUE } from '@/constants/cookies';
import { getGaMeasurementId, isAnalyticsActive } from '@/lib/analytics/config';
import { gtagEvent, isGtagAvailable } from '@/lib/analytics/gtag';
import { readAnalyticsConsent } from '@/lib/cookieConsent';

export type GaEventParams = Record<string, string | number | boolean | undefined>;

const COOKIE_CONSENT_GRANT_RETRY_MS = 100;
const COOKIE_CONSENT_GRANT_MAX_ATTEMPTS = 50;

function resolveLocale(): string {
  if (typeof document === 'undefined') return 'en';
  return document.documentElement.lang || 'en';
}

function compactParams(
  params: GaEventParams,
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    out[key] = value;
  }
  return out;
}

/**
 * Send a custom GA4 event when analytics is active, a measurement ID is set,
 * gtag is present, and analytics consent is granted.
 *
 * Hard-gated (not always-load Consent Mode): gtag is absent until grant, so
 * denied-only visitors never reach this path. Exception: `cookie_consent` with
 * `denied` may fire when gtag is already loaded (withdraw after prior grant).
 * First-visit reject (gtag never loaded) is a documented no-op — see task 22.
 */
export function trackEvent(eventName: GaEventName, params: GaEventParams = {}): void {
  if (!isAnalyticsActive() || !getGaMeasurementId()) return;
  if (!isGtagAvailable()) return;

  const isConsentDeniedEvent =
    eventName === GA_EVENTS.cookieConsent &&
    params[GA_PARAMS.choice] === GA_CONSENT_CHOICE.denied;

  if (!isConsentDeniedEvent && readAnalyticsConsent() !== COOKIES_CONSENT_VALUE.granted) {
    return;
  }

  gtagEvent(
    eventName,
    compactParams({
      ...params,
      [GA_PARAMS.locale]: resolveLocale(),
    }),
  );
}

export function trackOutboundClick({
  href,
  linkPlacement,
}: {
  href: string;
  linkPlacement: GaLinkPlacement;
}): void {
  let linkUrl = href;
  let linkDomain = '';

  try {
    const url = new URL(href, typeof window !== 'undefined' ? window.location.href : undefined);
    linkUrl = url.href;
    linkDomain = url.hostname;
  } catch {
    return;
  }

  trackEvent(GA_EVENTS.outboundClick, {
    [GA_PARAMS.linkUrl]: linkUrl,
    [GA_PARAMS.linkDomain]: linkDomain,
    [GA_PARAMS.linkPlacement]: linkPlacement,
  });
}

/**
 * Fire `cookie_consent`. For `granted`, retries until gtag is ready after the
 * gate mounts the GA script. For `denied`, fires only if gtag is already loaded
 * (first-visit reject is a documented gap).
 */
export function trackCookieConsent({
  choice,
  source,
}: {
  choice: GaConsentChoice;
  source: GaConsentSource;
}): void {
  const params = {
    [GA_PARAMS.choice]: choice,
    [GA_PARAMS.source]: source,
  };

  if (choice === GA_CONSENT_CHOICE.denied) {
    trackEvent(GA_EVENTS.cookieConsent, params);
    return;
  }

  let attempts = 0;
  const tryFire = () => {
    attempts += 1;
    if (isGtagAvailable()) {
      trackEvent(GA_EVENTS.cookieConsent, params);
      return;
    }
    if (attempts >= COOKIE_CONSENT_GRANT_MAX_ATTEMPTS) return;
    window.setTimeout(tryFire, COOKIE_CONSENT_GRANT_RETRY_MS);
  };

  tryFire();
}
